import React, { useState, useEffect } from "react";
import { makeApi, URLS } from "Utils/apiURL";
import { mapAiNotes } from "Utils/AiNotesUtils";

const COLOR_CLASS_MAP = {
  red: "kpTagRed",
  amber: "kpTagAmber",
  green: "kpTagGreen",
  gray: "kpTagGray"
};

const AiNotesTab = ({ call = {} }) => {
  const { callSid = "" } = call;
  const [aiData, setAiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kpExpanded, setKpExpanded] = useState(false);
  const [objExpanded, setObjExpanded] = useState(false);

  useEffect(() => {
    if (callSid) {
      fetchAiNotes();
    }
  }, [callSid]);

  const fetchAiNotes = async () => {
    setLoading(true);
    setAiData(null);
    try {
      const config = {
        url: `${URLS.getAiCallNotes}?callSid=${callSid}`,
        method: "GET"
      };
      const res = await makeApi(config);
      const { data = {} } = res || {};
      if (data?.aiNotes?.aiNotes) {
        setAiData(mapAiNotes(data.aiNotes.aiNotes));
      }
    } catch (e) {
      // silent fail
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="aiNotesTabLoading">
        <div className="aiNotesTabLoadingIcon">
          <i className="material-icons aiNotesTabSpinIcon">refresh</i>
        </div>
        <p className="aiNotesTabLoadingTitle">Loading AI notes...</p>
      </div>
    );
  }

  if (!aiData) {
    return (
      <div className="aiNotesTabEmpty">
        <div className="aiNotesTabEmptyIcon">
          <i className="material-icons-outlined">stars</i>
        </div>
        <p className="aiNotesTabEmptyTitle">AI notes not available for this call</p>
      </div>
    );
  }

  const visibleKeyPoints = kpExpanded ? aiData.keyPoints : aiData.keyPoints.slice(0, 3);
  const visibleObjections = objExpanded ? aiData.objections : aiData.objections.slice(0, 2);

  return (
    <div className="aiNotesTab">
      {aiData.summary && (
        <div className="aiNotesTabBlock">
          <div className="aiNotesTabAiLabel">
            <i className="material-icons">stars</i>
            <span>AI Summary</span>
          </div>
          <p className="aiNotesTabSummary">{aiData.summary}</p>
        </div>
      )}

      {aiData.keyPoints.length > 0 && (
        <div className="aiNotesTabBlock">
          <div className="aiNotesTabAiLabel">
            <i className="material-icons">stars</i>
            <span>Key Points</span>
          </div>
          <div className="aiNotesTabTable">
            {visibleKeyPoints.map((point, i) => (
              <div key={i} className="aiNotesTabTableRow">
                <div className="aiNotesTabTableTag">
                  <span className={`kpTag ${COLOR_CLASS_MAP[point.color] || COLOR_CLASS_MAP.gray}`}>
                    {point.cat}
                  </span>
                </div>
                <span className="aiNotesTabTableText">{point.text}</span>
              </div>
            ))}
            {aiData.keyPoints.length > 3 && (
              <button
                className="aiNotesTabExpandBtn"
                onClick={() => setKpExpanded(e => !e)}
              >
                <i className="material-icons">
                  {kpExpanded ? "expand_less" : "expand_more"}
                </i>
                {kpExpanded ? "Show less" : `Show ${aiData.keyPoints.length - 3} more`}
              </button>
            )}
          </div>
        </div>
      )}

      {aiData.objections.length > 0 && (
        <div className="aiNotesTabBlock">
          <div className="aiNotesTabAiLabel">
            <i className="material-icons">stars</i>
            <span>Objections</span>
          </div>
          <div className="aiNotesTabTable">
            {visibleObjections.map((obj, i) => (
              <div key={i} className="aiNotesTabTableRow aiNotesTabTableRowObj">
                <div className="aiNotesTabObjDot" />
                <div>
                  <p className="aiNotesTabObjLabel">{obj.label}</p>
                  <p className="aiNotesTabObjDesc">{obj.desc}</p>
                </div>
              </div>
            ))}
            {aiData.objections.length > 2 && (
              <button
                className="aiNotesTabExpandBtn"
                onClick={() => setObjExpanded(e => !e)}
              >
                <i className="material-icons">
                  {objExpanded ? "expand_less" : "expand_more"}
                </i>
                {objExpanded ? "Show less" : `Show ${aiData.objections.length - 2} more`}
              </button>
            )}
          </div>
        </div>
      )}

      {aiData.nextSteps.length > 0 && (
        <div className="aiNotesTabBlock">
          <div className="aiNotesTabAiLabel">
            <i className="material-icons">stars</i>
            <span>Suggested Next Steps</span>
          </div>
          {aiData.nextSteps.map((step, i) => (
            <div key={i} className="aiNotesTabNextStep">
              <p className="aiNotesTabNextStepText">{step}</p>
            </div>
          ))}
        </div>
      )}

      {aiData.actionItems.length > 0 && (
        <div className="aiNotesTabBlock">
          <div className="aiNotesTabAiLabel">
            <i className="material-icons">stars</i>
            <span>Action Items</span>
          </div>
          <div className="aiNotesTabTable">
            {aiData.actionItems.map((item, i) => (
              <div key={i} className="aiNotesTabTableRow aiNotesTabTableRowAction">
                <div className="aiNotesTabObjDot" />
                <div>
                  <p className="aiNotesTabObjLabel">{item.action}</p>
                  {item.owner && (
                    <p className="aiNotesTabObjDesc">Owner: {item.owner}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {aiData.topics.length > 0 && (
        <div className="aiNotesTabBlock">
          <div className="aiNotesTabAiLabel">
            <i className="material-icons">stars</i>
            <span>Topics Discussed</span>
          </div>
          <div className="aiNotesTabTable">
            {aiData.topics.map((topic, i) => (
              <div key={i} className="aiNotesTabTableRow aiNotesTabTableRowTopic">
                <div>
                  <p className="aiNotesTabObjLabel">{topic.heading}</p>
                  <p className="aiNotesTabObjDesc">{topic.summary || topic.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AiNotesTab;
